import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enCA } from 'date-fns/locale';
import i18n from '../i18n';
import { badgesAPI, usersAPI, centersAPI } from '../utils/api';
import { useLocalBadgeSocket } from '../utils/localBadgeSocket';

const normalizeUid = (value = '') =>
  String(value)
    .trim()
    .toUpperCase()
    .replace(/^0X/, '')
    .replace(/[^A-Z0-9]/g, '');

const BadgesPage = () => {
  const { t } = useTranslation();
  const locale = i18n.language.startsWith('fr') ? fr : enCA;

  const [badges, setBadges] = useState([]);
  const [users, setUsers] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');

  const [manualUid, setManualUid] = useState('');
  const [manualUserId, setManualUserId] = useState('');
  const [manualCenterIds, setManualCenterIds] = useState([]);
  const [manualLabel, setManualLabel] = useState('');
  const [lastDetectedUid, setLastDetectedUid] = useState('');
  const [readArmed, setReadArmed] = useState(false);
  const [readAfterSeq, setReadAfterSeq] = useState(0);
  const { connected: socketConnected, lastScan: socketScan, socketError, socketUrl } = useLocalBadgeSocket();

  const [rowUserSelection, setRowUserSelection] = useState({});
  const selectedManualUser = useMemo(
    () => users.find((u) => u.id === manualUserId) || null,
    [users, manualUserId]
  );
  const selectedManualUserCenterIds = useMemo(() => (
    Array.isArray(selectedManualUser?.center_ids)
      ? selectedManualUser.center_ids
      : (selectedManualUser?.center_id ? [selectedManualUser.center_id] : [])
  ), [selectedManualUser]);
  const selectableCenters = useMemo(() => (
    manualUserId
      ? centers.filter((c) => selectedManualUserCenterIds.includes(c.id))
      : centers
  ), [manualUserId, centers, selectedManualUserCenterIds]);

  const handleDetectedUid = useCallback((uid, sourceLabel) => {
    const normalized = normalizeUid(uid);
    if (!normalized) return;
    setLastDetectedUid(normalized);
    setManualUid(normalized);
    setSuccess(`Badge détecté (${sourceLabel}): ${normalized}`);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (activeFilter) params.active = activeFilter;
      if (assignedFilter) params.assigned = assignedFilter;

      const [badgesRes, usersRes, centersRes] = await Promise.all([
        badgesAPI.getAll(params),
        usersAPI.getAll(),
        centersAPI.getAll(),
      ]);

      const badgesList = Array.isArray(badgesRes.data) ? badgesRes.data : [];
      const usersList = Array.isArray(usersRes.data?.users) ? usersRes.data.users : [];
      const centersList = Array.isArray(centersRes.data) ? centersRes.data : [];

      setBadges(badgesList);
      setUsers(usersList.filter((u) => u.is_active !== false));
      setCenters(centersList);

      const nextSelection = {};
      badgesList.forEach((badge) => {
        nextSelection[badge.id] = badge.active_assignment?.user?.id || '';
      });
      setRowUserSelection(nextSelection);
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [activeFilter, assignedFilter, search, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!manualUserId) {
      setManualCenterIds([]);
      return;
    }

    // Auto-populate centers from selected user profile (Personnel/Patients multi-centers)
    setManualCenterIds(selectedManualUserCenterIds);
  }, [manualUserId, selectedManualUserCenterIds]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  const upsertAndAssignFromUid = async ({ uid, userId, centerIds, label }) => {
    const cardUid = normalizeUid(uid);
    if (!cardUid) throw new Error('UID badge invalide');
    await badgesAPI.create({
      cardUid,
      userId: userId || undefined,
      centerId: centerIds?.[0] || undefined,
      centerIds: centerIds?.length ? centerIds : undefined,
      label: label || undefined,
    });
  };

  const handleManualLink = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const uid = normalizeUid(manualUid);
    if (!uid) {
      setError('UID badge requis');
      return;
    }
    if (!manualUserId) {
      setError('Sélectionnez un utilisateur');
      return;
    }

    setSaving(true);
    try {
      await upsertAndAssignFromUid({
        uid,
        userId: manualUserId,
        centerIds: manualCenterIds,
        label: manualLabel,
      });
      setSuccess(`Badge ${uid} assigné avec succès`);
      setManualUid('');
      setManualLabel('');
      await load();
    } catch (err) {
      const apiMessage = err.response?.data?.message || '';
      if (err.response?.status === 409) {
        const msg = apiMessage.toLowerCase();
        if (msg.includes('déjà actif')) {
          setError('UID déjà actif');
          return;
        }
        if (msg.includes('désactivé')) {
          setError('UID existant désactivé, veuillez réactiver le badge existant');
          return;
        }
      }
      setError(apiMessage || err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const armRead = () => {
    if (!socketConnected) {
      setError('Socket local non connecte');
      return;
    }
    setError('');
    setSuccess('');
    setReadAfterSeq(socketScan?.seq || 0);
    setReadArmed(true);
  };

  useEffect(() => {
    if (!readArmed || !socketScan?.uid) return;
    if ((socketScan.seq || 0) <= readAfterSeq) return;

    handleDetectedUid(socketScan.uid, `SOCKET:${socketScan.eventName || 'message'}`);
    setReadArmed(false);
  }, [readArmed, readAfterSeq, socketScan, handleDetectedUid]);

  const handleAssignExisting = async (badgeId) => {
    const userId = rowUserSelection[badgeId];
    if (!userId) {
      setError('Sélectionnez un utilisateur pour l’assignation');
      return;
    }

    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await badgesAPI.assign(badgeId, { userId, replaceExisting: true });
      setSuccess('Assignation mise à jour');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (badgeId) => {
    if (!window.confirm('Révoquer l’assignation de ce badge ?')) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await badgesAPI.revoke(badgeId, { reason: 'Révoqué depuis interface web' });
      setSuccess('Assignation révoquée');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (badge) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      if (badge.is_active) await badgesAPI.deactivate(badge.id);
      else await badgesAPI.activate(badge.id);
      setSuccess(badge.is_active ? 'Badge désactivé' : 'Badge réactivé');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBadge = async (badge) => {
    if (!window.confirm(`Supprimer définitivement le badge ${badge.card_uid} ?`)) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await badgesAPI.remove(badge.id);
      setSuccess('Badge supprimé');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Badges</h2>
          <p className="text-muted">Gérer les badges, les assignations et la lecture via le socket local</p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading || saving}>
          Recharger
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>⚠ {error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>✓ {success}</div>}

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Lecture badge</h3>
        <div className="toolbar">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%' }}>
            <button type="button" className="btn btn-primary" onClick={armRead} disabled={readArmed}>
              {readArmed ? 'En attente d une nouvelle lecture...' : 'Lire'}
            </button>
            <span className={`badge ${socketConnected ? 'badge-success' : 'badge-danger'}`}>
              Socket local {socketConnected ? 'connecte' : 'deconnecte'}
            </span>
          </div>
        </div>
        {lastDetectedUid && (
          <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
            Dernier UID détecté: <strong>{lastDetectedUid}</strong>
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
          Source: socket.io local ({socketUrl}).
          {socketError ? ` Erreur: ${socketError}` : ''}
        </div>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Lier un badge à un utilisateur</h3>
        <form onSubmit={handleManualLink}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">UID badge *</label>
              <input
                className="form-input"
                placeholder="Cliquez sur Lire puis passez une carte"
                value={manualUid}
                readOnly
              />
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                UID renseigne uniquement par nouvelle lecture socket.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Utilisateur *</label>
              <select
                className="form-select"
                value={manualUserId}
                onChange={(e) => setManualUserId(e.target.value)}
              >
                <option value="">— Sélectionner —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} {u.employee_number ? `(${u.employee_number})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Libellé badge</label>
              <input
                className="form-input"
                placeholder="Ex: Badge principal"
                value={manualLabel}
                onChange={(e) => setManualLabel(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Centre (optionnel)</label>
              <select
                className="form-select"
                multiple
                size={Math.min(6, Math.max(3, selectableCenters.length || 3))}
                value={manualCenterIds}
                onChange={(e) => setManualCenterIds(Array.from(e.target.selectedOptions).map((o) => o.value))}
              >
                {selectableCenters.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 6 }}>
                {manualUserId
                  ? 'Centres limités à ceux de l’utilisateur sélectionné (Ctrl/Cmd + clic)'
                  : 'Sélectionnez d’abord un utilisateur pour filtrer les centres autorisés'}
              </div>
            </div>
          </div>
          <button type="submit" className="btn btn-success" disabled={saving}>
            {saving ? t('common.loading') : 'Lier badge'}
          </button>
        </form>
      </div>

      <div className="card card-form" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 10 }}>Filtres badges</h3>
        <form className="toolbar" onSubmit={handleSearchSubmit}>
          <div className="search-input-wrapper" style={{ minWidth: 260 }}>
            <input
              className="form-input search-input"
              placeholder="Rechercher UID, libellé..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="form-select" style={{ width: 180 }} value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
            <option value="">Tous états</option>
            <option value="true">Actifs</option>
            <option value="false">Désactivés</option>
          </select>
          <select className="form-select" style={{ width: 220 }} value={assignedFilter} onChange={(e) => setAssignedFilter(e.target.value)}>
            <option value="">Toutes assignations</option>
            <option value="true">Assignés</option>
            <option value="false">Non assignés</option>
          </select>
          <button className="btn btn-primary" type="submit">Filtrer</button>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Tous les badges ({badges.length})</span>
        </div>
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 36, textAlign: 'center' }}>
              <div className="spinner" style={{ margin: 'auto' }} />
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>UID</th>
                  <th>Libellé</th>
                  <th>Utilisateur assigné</th>
                  <th>Statut</th>
                  <th>Centre(s)</th>
                  <th>Mis à jour</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!badges.length && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 24 }}>
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
                {badges.map((badge) => {
                  const assignedUser = badge.active_assignment?.user || null;
                  const allowedCenterIds = Array.isArray(badge.allowed_center_ids) && badge.allowed_center_ids.length
                    ? badge.allowed_center_ids
                    : (badge.center_id ? [badge.center_id] : []);
                  const badgeCenters = centers.filter((c) => allowedCenterIds.includes(c.id));

                  return (
                    <tr key={badge.id}>
                      <td>
                        <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{badge.card_uid}</div>
                        {badge.external_ref && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{badge.external_ref}</div>}
                      </td>
                      <td>{badge.label || '—'}</td>
                      <td>
                        {assignedUser ? (
                          <div>
                            <div style={{ fontWeight: 600 }}>{assignedUser.first_name} {assignedUser.last_name}</div>
                            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{assignedUser.employee_number || '—'}</div>
                          </div>
                        ) : (
                          <span className="badge badge-gray">Non assigné</span>
                        )}
                      </td>
                      <td>
                        {badge.is_active
                          ? <span className="badge badge-success">Actif</span>
                          : <span className="badge badge-danger">Désactivé</span>}
                      </td>
                      <td>
                        {badgeCenters.length
                          ? badgeCenters.map((c) => c.name).join(', ')
                          : '—'}
                      </td>
                      <td>{badge.updated_at ? format(new Date(badge.updated_at), 'dd/MM/yyyy HH:mm', { locale }) : '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className={`btn ${badge.is_active ? 'btn-outline' : 'btn-success'}`}
                            onClick={() => handleToggleActive(badge)}
                            disabled={saving}
                          >
                            {badge.is_active ? 'Désactiver' : 'Réactiver'}
                          </button>
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDeleteBadge(badge)}
                            disabled={saving}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgesPage;
